import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';

import apiService from '../services/apiService';

import { getConfig } from '../config/appConfig';

import toast from 'react-hot-toast';

import { useAuth } from './AuthContext';

import {

  tryAcquireCheckoutLock,

  markCheckoutLockDone,

  resetCheckoutLockForRetry,

  beginCheckoutVerifyInflight,

  endCheckoutVerifyInflight,

} from '../utils/checkoutLock';

import {

  getCheckoutReturnParams,

  clearCheckoutReturnParamsFromUrl,

  clearStoredCheckoutContext,

  getStoredCheckoutBillingCycle,

  CHECKOUT_PLAN_STORAGE_KEY,

  CHECKOUT_SESSION_STORAGE_KEY,

} from '../utils/checkoutReturnParams';

import { isPaddleTransactionCheckoutId } from '../utils/checkoutVerifyRouting';

import subscriptionStatusRetry from '../utils/subscriptionStatusRetry';

import { overlayDailyPromptMeter, isFreeLikePlan } from '../utils/planCatalogUtils';



const { fetchSubscriptionStatusWithRetry } = subscriptionStatusRetry;



const SubscriptionContext = createContext();



export const useSubscription = () => {

  const context = useContext(SubscriptionContext);

  if (!context) {

    throw new Error('useSubscription must be used within a SubscriptionProvider');

  }

  return context;

};



export const SubscriptionProvider = ({ children }) => {

  const [subscription, setSubscription] = useState(null);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  const [billingHistory, setBillingHistory] = useState([]);

  const [usageData, setUsageData] = useState(null);

  

  // Use refs to prevent multiple initializations

  const isInitializedRef = useRef(false);

  const usageDataRef = useRef(null);



  const { isAuthenticated, loading: authLoading, user } = useAuth();



  // AD -11072025 -start

  // Load subscription data with proper state management - always fetch fresh from database

  const loadSubscriptionData = useCallback(async (skipLoadingState = false) => {

    if (!isAuthenticated) return;

    try {

      // Only set loading state if not skipped (for initial load, parent manages loading state)

      if (!skipLoadingState) {

        setLoading(true);

      }

      setError(null);

      

      const checkoutParams = getCheckoutReturnParams();

      const isSuccessPage = window.location.pathname.toLowerCase().endsWith('/success') || 

                            window.location.hash.toLowerCase().includes('/success');

      if (checkoutParams.hasCheckoutReturn && !isSuccessPage) {

        clearCheckoutReturnParamsFromUrl();

      }

      

      // Clear any cached subscription data to force fresh load from database

      setSubscription(null);

      

        // Try to load real data from API (database) - NO CACHE

      try {

        // ***** Code Start BH07092026_2145 - Retry status for new Community Edition provisioning

        // Fresh register/login can race ahead of subscription row visibility.

        const subscriptionData = await fetchSubscriptionStatusWithRetry(

          () => apiService.getSubscriptionStatus(true)

        );

        // ***** Code End BH07092026_2145

        

        // Check subscription status - handle expired/no_subscription cases

        const status = subscriptionData?.status?.toLowerCase();

        const planName = subscriptionData?.plan_name;

        

        // If status is 'no_subscription' or plan_name is null/undefined (and not expired), treat as no subscription

        // For 'expired' status, keep the subscription data so we can display expiration message

        if (status === 'no_subscription' || (!planName && status !== 'expired') || planName === 'Loading...') {

          setSubscription(null);  // Clear subscription to show "no subscription" state

          if (!skipLoadingState) {

            setLoading(false);

          }

          return;

        }

        

        // For expired subscriptions, keep the data to show expiration message

        // Even if plan_name is null, we still want to show expiration message

        if (status === 'expired') {

          // Process expired subscription data (plan_name may be null)

          const subscriptionUpdate = overlayDailyPromptMeter({

            ...subscriptionData,

            // Use EXACT values from API response - NO fallbacks, NO defaults

            total_tokens_used: subscriptionData.total_tokens_used,

            usage_limit: subscriptionData.usage_limit,

            remaining_tokens: subscriptionData.remaining_tokens,

            current_usage: subscriptionData.current_usage,

            monthly_limit: subscriptionData.monthly_limit,

            next_billing_date: subscriptionData.next_billing_date,

            end_date: subscriptionData.end_date,

            status: 'expired'  // Ensure status is set to expired

          }, usageDataRef.current);

          setSubscription(subscriptionUpdate);

          if (!skipLoadingState) {

            setLoading(false);

          }

          return;

        }

        

        // Valid subscription found - process it

        if (subscriptionData && planName) {

          // Use EXACT values from database - NO fallbacks, NO defaults, NO computed values

          // Only use what the API returns from the database

          const subscriptionUpdate = overlayDailyPromptMeter({

            ...subscriptionData,

            // Use EXACT values from API response - NO fallbacks, NO defaults, NO alternative field names

            // If API returns null/undefined, keep as null/undefined

            total_tokens_used: subscriptionData.total_tokens_used,

            usage_limit: subscriptionData.usage_limit,

            remaining_tokens: subscriptionData.remaining_tokens,

            // Backward compatibility fields - use exact API values only

            current_usage: subscriptionData.current_usage,  // Use exact value from API, NO fallback to total_tokens_used

            monthly_limit: subscriptionData.monthly_limit,  // Use exact value from API, NO fallback to usage_limit

            // Use exact database values - NO computation, NO fallbacks

            next_billing_date: subscriptionData.next_billing_date,

            end_date: subscriptionData.end_date

          }, usageDataRef.current);

          

          setSubscription(subscriptionUpdate);

          // Only set loading to false if not skipped (parent manages loading state during initial load)

          if (!skipLoadingState) {

            setLoading(false);

          }

          return;

        }

        

        // If API call succeeded but data format is unexpected, show no subscription

        setSubscription(null);

        if (!skipLoadingState) {

          setLoading(false);

        }

        return;

      } catch (apiError) {

        // Handle different types of errors

        if (apiError.code === 'CONNECTION_TIMEOUT' || apiError.message?.includes('timeout')) {

          // Connection timeout - DO NOT use fallback, show no subscription instead

          setSubscription(null);

          if (!skipLoadingState) {

            setLoading(false);

          }

          return;

        } else if (apiError.status === 401 || apiError.message?.includes('Invalid API key')) {

          setSubscription({

            plan_name: 'Authentication Required',

            status: 'auth_required',

            current_usage: 0,

            monthly_limit: 0,

            billing_cycle: 'monthly',

            next_billing_date: null,

            price: 0

          });

          if (!skipLoadingState) {

            setLoading(false);

          }

          return;

        }

        

        // For other API errors, show no subscription instead of fallback

        setSubscription(null);

        if (!skipLoadingState) {

          setLoading(false);

        }

        return;

      }

      

    } catch (error) {

      // DO NOT use hardcoded fallback - show no subscription instead

      // This ensures users see accurate state from database, not fake data

      setSubscription(null);

      if (!skipLoadingState) {

        setLoading(false);

      }

    }

  }, [isAuthenticated]);

  // AD -11072025 -end



  // AD -11072025 -start

  // Load billing history - always fetch fresh from database

  const loadBillingHistory = useCallback(async () => {

    if (!isAuthenticated) return;

    try {

      // Try to load real billing data from API (database) - NO CACHE

      const billingData = await apiService.getBillingHistory(true); // Pass true for fresh fetch

      let invoices = billingData.invoices || billingData.billing_history || [];

      const userEmail = (user?.email || '').toLowerCase().trim();

      if (userEmail && Array.isArray(invoices)) {

        invoices = invoices.filter((inv) => {

          const invEmail = (

            inv.customer_email ||

            inv.metadata?.user_email ||

            inv.metadata?.customer_email ||

            ''

          )

            .toLowerCase()

            .trim();

          return !invEmail || invEmail === userEmail;

        });

      }

      setBillingHistory(invoices);

    } catch (error) {

      // Set empty array instead of hardcoded data

      setBillingHistory([]);

    }

  }, [isAuthenticated, user?.email]);

  // AD -11072025 -end



  // AD -11072025 -start

  // Load usage data - always fetch fresh from database

  const loadUsageData = useCallback(async () => {

    if (!isAuthenticated) return;

    try {

      // Try to load real usage data from API (database) - NO CACHE

      const usageData = await apiService.getUsageData(true); // Pass true for fresh fetch

      usageDataRef.current = usageData;

      setUsageData(usageData);

      setSubscription((prev) => overlayDailyPromptMeter(prev, usageData));

    } catch (error) {

      // Set minimal data instead of hardcoded values

      setUsageData({

        current_usage: 0,

        monthly_limit: 0,

        usage_percentage: 0,

        tokens_remaining: 0,

        reset_date: new Date().toISOString()

      });

    }

  }, [isAuthenticated]);

  // AD -11072025 -end



  // AD -11072025 -start

  // Initialize data loading - ALWAYS fetch fresh data from database on page load

  useEffect(() => {

    if (authLoading) {

      return;

    }



    if (!isAuthenticated) {

      // Clear data and stop loading since we are not authenticated

      setSubscription(null);

      setBillingHistory([]);

      setUsageData(null);

      setLoading(false);

      isInitializedRef.current = false;

      return;

    }



    // Prevent double initialization (React StrictMode in dev causes double mount)

    if (isInitializedRef.current) {

      return;

    }

    

    // Mark as initialized immediately to prevent duplicate calls

    isInitializedRef.current = true;

    

    // Always start with loading state true

    setLoading(true);

    setError(null);

    

    // Clear any cached data to ensure fresh fetch

    setSubscription(null);

    setBillingHistory([]);

    setUsageData(null);

    

    // Load status first (unlocks dashboard), then billing/usage in background

    // ***** Code Start BH10092026_1716 - Don't block shell on billing/usage

    const loadAllData = async () => {

      try {

        await loadSubscriptionData(true);

      } catch (error) {

        setError(error.message || 'Failed to load subscription data');

      } finally {

        // Unlock dashboard as soon as status settles

        setLoading(false);

      }

      // Secondary panels — parallel, non-blocking

      void Promise.all([loadBillingHistory(), loadUsageData()]).catch(() => {});

    };

    // ***** Code End BH10092026_1716

    

    loadAllData();

  // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [loadSubscriptionData, loadBillingHistory, loadUsageData, isAuthenticated, authLoading]); // applyCheckoutCredits is called inside loadAllData which is defined within this effect

  // AD -11072025 -end



  // Update subscription

  const updateSubscription = async (subscriptionData) => {

    if (!isAuthenticated) return;

    try {

      setLoading(true);

      setError(null);

      

      const updatedSubscription = await apiService.updateSubscription(subscriptionData);

      setSubscription(updatedSubscription);

      toast.success(getConfig('messages.success.subscriptionUpdated'));

      

      // Notify desktop app about subscription update

      if (getConfig('desktop.integrationEnabled')) {

        notifyDesktopApp('subscription_updated', {

          subscription: updatedSubscription,

          timestamp: Date.now(),

        });

      }

      

      return updatedSubscription;

    } catch (error) {

      setError(error.message || getConfig('messages.errors.genericError'));

      toast.error(error.message || getConfig('messages.errors.genericError'));

      throw error;

    } finally {

      setLoading(false);

    }

  };



  // Cancel subscription

  const cancelSubscription = async () => {

    if (!isAuthenticated) return;

    try {

      setLoading(true);

      setError(null);

      

      await apiService.cancelSubscription();

      

      // Reload subscription data to get updated status from backend

      await loadSubscriptionData();

      

      toast.success('Subscription cancelled successfully');

      

      // Notify desktop app about cancellation

      if (getConfig('desktop.integrationEnabled')) {

        notifyDesktopApp('subscription_cancelled', {

          timestamp: Date.now(),

        });

      }

      

    } catch (error) {

      setError(error.message || getConfig('messages.errors.genericError'));

      toast.error(error.message || getConfig('messages.errors.genericError'));

      throw error;

    } finally {

      setLoading(false);

    }

  };



  // Manual subscription update (for desktop app integration)

  const manualUpdateSubscription = useCallback(async (data) => {

    if (!isAuthenticated) return;

    try {

      setLoading(true);

      setError(null);

      

      const result = await apiService.manualUpdateSubscription(data);

      

      // Refresh subscription data

      await loadSubscriptionData();

      

      toast.success(getConfig('messages.success.subscriptionUpdated'));

      

      return result;

    } catch (error) {

      setError(error.message || getConfig('messages.errors.genericError'));

      toast.error(error.message || getConfig('messages.errors.genericError'));

      throw error;

    } finally {

      setLoading(false);

    }

  }, [loadSubscriptionData, isAuthenticated]);



  // ── Checkout Credit Application ──────────────────────────────────────────

  /**

   * Calls /api/v1/payments/verify-checkout-session with a 3-attempt retry loop.

   * The server endpoint is idempotent (DB-level lock via stripe_webhook_events).

   * The client lock (checkoutLock) prevents sending the request at all on repeats.

   *

   * @param {string} sessionId   Stripe checkout session ID

   * @param {string} planId      Plan identifier (e.g. 'pro_plan', 'addon_50k')

   * @param {string} lockKey     Lock key from tryAcquireCheckoutLock

   */

  const applyCheckoutCredits = useCallback(async (sessionId, planId, lockKey, billingCycle = 'monthly') => {

    const MAX_ATTEMPTS = 5;

    const BACKOFF_MS = [1000, 2000, 3000, 4000, 5000];  // up to ~15s total — handles Paddle webhook delay

    let lastErrorMessage = null;

    let lastResult = null;



    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {

      try {

        if (attempt > 0) {

          await new Promise((resolve) => setTimeout(resolve, BACKOFF_MS[attempt - 1]));

        }



        // Paddle che_* / txn_* → verify-transaction only (never verify-checkout-session → Stripe)

        if (!isPaddleTransactionCheckoutId(sessionId)) {

          lastResult = await apiService.verifyCheckoutSession({

            sessionId,

            planId,

            billingCycle,

          });

        } else {

          lastResult = await apiService.verifyPaddleTransaction({

            transactionId: sessionId,

            planId,

            billingCycle,

          });

        }

        const result = lastResult;



        // ***** Code Start BH29072026_1830 - 'ready' is an unpaid Paddle state

        // A transaction sits in draft/ready until the buyer actually pays, so accepting

        // 'ready' here marked an abandoned checkout as verified and switched the plan.

        const reportedStatus = String(

          result?.status || result?.payment_status || ''

        ).toLowerCase();

        const isVerified =

          result?.verified === true ||

          result?.success === true ||

          ['paid', 'completed', 'billed'].includes(reportedStatus);

        // ***** Code End BH29072026_1830



        if (isVerified) {

          markCheckoutLockDone(lockKey);

          await loadSubscriptionData(true);

          await loadUsageData();



          if (result.plan_type === 'addon' && result.tokens_added > 0) {

            toast.success(

              `✅ ${result.tokens_added.toLocaleString()} tokens added to your account!`,

              { duration: 6000 }

            );

          } else if (result.plan_type === 'addon' && result.already_processed) {

            /* already processed */

          } else if (result.plan_name || result.plan) {

            toast.success(

              `✅ Subscription updated to ${result.plan_name || result.plan}`,

              { duration: 5000 }

            );

          } else if (result.message) {

            toast.success(result.message, { duration: 5000 });

          }

          return lastResult;

        }



        lastErrorMessage = result?.message || 'Payment not yet confirmed';

        if (attempt < MAX_ATTEMPTS - 1) continue;

        resetCheckoutLockForRetry(lockKey);

        toast.error(

          lastErrorMessage ||

            'Payment verification is taking longer than expected. Please refresh in a moment.'

        );

      } catch (err) {

        lastErrorMessage =

          err?.message ||

          err?.data?.detail ||

          (typeof err?.data === 'string' ? err.data : null);

        const status = err?.status || err?.response?.status;

        const noRetry = status >= 500 || status === 401 || status === 403;

        if (noRetry || attempt === MAX_ATTEMPTS - 1) {

          resetCheckoutLockForRetry(lockKey);

          if (attempt === MAX_ATTEMPTS - 1 || noRetry) {

            toast.error(

              lastErrorMessage ||

                'Could not verify your payment. Please contact support if tokens are missing.'

            );

          }

          if (noRetry) break;

        }

      }

    }

    return lastResult;

  }, [loadSubscriptionData, loadUsageData]);

  // ── End Checkout Credit Application ──────────────────────────────────────



  // Refresh all data

  const refreshData = useCallback(async () => {

    if (!isAuthenticated) return;

    try {

      // Load real data from API

      await loadSubscriptionData();

      await loadBillingHistory();

      await loadUsageData();

    } catch (error) {

      // Still try to load fallback data

      loadSubscriptionData();

      loadBillingHistory();

      loadUsageData();

    }

  }, [loadSubscriptionData, loadBillingHistory, loadUsageData, isAuthenticated]);



  /** Verify payment and refresh subscription (Success page / dashboard return). */

  const completeCheckoutReturn = useCallback(

    async (checkoutId, planId, billingCycle = 'monthly') => {

      if (!isAuthenticated) return null;

      if (!checkoutId) {

        await refreshData();

        return null;

      }

      if (!beginCheckoutVerifyInflight(checkoutId)) {

        await loadSubscriptionData(true);

        return { processed: false, skipped: true };

      }

      try {

        const { acquired, lockKey } = tryAcquireCheckoutLock(checkoutId);

        let verifyResult = null;

        if (acquired) {

          verifyResult = await applyCheckoutCredits(checkoutId, planId, lockKey, billingCycle);

        } else {

          await loadSubscriptionData(true);

          await loadUsageData();

        }

        clearCheckoutReturnParamsFromUrl();

        return { processed: acquired, verifyResult };

      } finally {

        endCheckoutVerifyInflight(checkoutId);

      }

    },

    [

      isAuthenticated,

      applyCheckoutCredits,

      loadSubscriptionData,

      loadUsageData,

      refreshData,

    ]

  );



  // Run checkout verification when URL has checkout params (not on /success — SuccessPage owns that)

  useEffect(() => {

    if (!isAuthenticated || authLoading) return;



    const isSuccessRoute = () => {

      const pathname = window.location.pathname.toLowerCase();

      const hash = (window.location.hash || '').toLowerCase();

      return pathname.endsWith('/success') || hash.startsWith('#/success') || hash.includes('/success?');

    };



    const processCheckoutReturn = async () => {

      if (isSuccessRoute()) return;

      const returnParams = getCheckoutReturnParams();

      const storedSessionId = sessionStorage.getItem(CHECKOUT_SESSION_STORAGE_KEY);

      const storedPlanId = sessionStorage.getItem(CHECKOUT_PLAN_STORAGE_KEY);

      const effectiveCheckoutId = returnParams.checkoutId || storedSessionId;

      const effectivePlanId = returnParams.planId || storedPlanId;

      const effectiveBillingCycle =

        returnParams.billingCycle || getStoredCheckoutBillingCycle() || 'monthly';



      // ***** Code Start BH29072026_1830 - A stored session id is not proof of payment

      // storeCheckoutContext() runs BEFORE the Paddle overlay opens, so a bare

      // storedSessionId was enough to start verification on the next mount/hashchange —

      // closing the overlay without paying then switched the plan. Require an actual

      // return signal in the URL; the stored id only supplies the id when one is missing.

      const hasCheckoutReturnSignal =

        returnParams.success || returnParams.transactionId || returnParams.sessionId;



      const shouldVerify = effectiveCheckoutId && hasCheckoutReturnSignal;



      if (!shouldVerify) return;

      // ***** Code End BH29072026_1830



      clearStoredCheckoutContext();

      await completeCheckoutReturn(

        effectiveCheckoutId,

        effectivePlanId,

        effectiveBillingCycle

      );

    };



    processCheckoutReturn();

    window.addEventListener('hashchange', processCheckoutReturn);

    return () => window.removeEventListener('hashchange', processCheckoutReturn);

  }, [isAuthenticated, authLoading, completeCheckoutReturn]);

  

  // Force refresh subscription data (clears cache)

  const forceRefresh = useCallback(async () => {

    setSubscription(null);

    await loadSubscriptionData();

  }, [loadSubscriptionData]);



  // Get plan configuration

  const getPlanConfig = (planId) => {

    return getConfig(`plans.${planId}`);

  };



  // ***** Code Start BH25082026_1325 - Prefer status API trial flags (Community-aware)

  const isOnTrial = () => {

    if (subscription?.trial_expired === true) return false;

    if (subscription?.is_free_trial === true) return true;

    if (subscription?.trial_active === true) return true;

    const name = String(subscription?.plan_name || '').toLowerCase();

    const type = String(subscription?.plan_type || '').toLowerCase();

    if (/(developer|corporate|enterprise|professional)/.test(name)) return false;

    if (type === 'pay_as_you_go' || type === 'usage_based') return false;

    return (

      type === 'trial' ||

      type === 'free' ||

      type === 'starter' ||

      name.includes('community') ||

      name.includes('free trial') ||

      name.includes('starter')

    );

  };



  const isTrialExpired = () => {

    if (subscription?.trial_expired === true) return true;

    if (!subscription?.trial_end) return false;

    const end = new Date(subscription.trial_end);

    if (Number.isNaN(end.getTime())) return false;

    return end < new Date();

  };



  const getTrialDaysRemaining = () => {

    if (typeof subscription?.trial_days_remaining === 'number') {

      return Math.max(0, Math.floor(subscription.trial_days_remaining));

    }

    return 0;

  };

  // ***** Code End BH25082026_1325



  // Get trial end date

  const getTrialEndDate = () => {

    return subscription?.trial_end;

  };



  // Check if user has active subscription

  const hasActiveSubscription = () => {

    return subscription?.status === 'active' && subscription?.price > 0;

  };



  // Get usage percentage

  const getUsagePercentage = () => {
    const planName = subscription?.plan_name?.toLowerCase() || '';
    if (planName.includes('pay') && planName.includes('go')) {
      return 0; // Pay-as-you-go has no fixed allotment
    }

    // Direct usage percentage from usage-limit JSON if returned
    const directPct = usageData?.usage_percentage ?? subscription?.usage_percentage;
    if (directPct != null && Number.isFinite(Number(directPct))) {
      return Math.max(0, Math.min(100, Number(directPct)));
    }

    const tokensUsed = Number(
      usageData?.current_usage ?? usageData?.total_tokens_used ?? subscription?.current_usage ?? subscription?.total_tokens_used ?? 0
    ) || 0;
    const remaining = Number(usageData?.remaining_tokens ?? subscription?.remaining_tokens);
    const monthlyLimit = Number(usageData?.monthly_limit ?? subscription?.monthly_limit ?? usageData?.usage_limit ?? subscription?.usage_limit);

    let pool = 0;
    if (Number.isFinite(monthlyLimit) && monthlyLimit > 0) {
      pool = monthlyLimit;
    } else if (Number.isFinite(remaining) && remaining >= 0) {
      pool = tokensUsed + remaining;
    }

    if (!pool || pool <= 0) return 0;
    return Math.max(0, Math.min(100, (tokensUsed / pool) * 100));
  };

  const getRemainingRequests = () => {
    // Authoritative remaining tokens directly from usage-limit JSON if returned
    const rem = usageData?.remaining_tokens ?? subscription?.remaining_tokens;
    if (rem === null || rem === undefined) {
      return null;
    }

    const numericValue = Number(rem);
    if (Number.isNaN(numericValue)) {
      return rem;
    }

    return Math.max(0, numericValue);
  };
  // Notify desktop app

  const notifyDesktopApp = (eventType, data) => {

    try {

      // Method 1: PostMessage to parent window

      if (window.opener) {

        window.opener.postMessage({

          type: eventType,

          data: data,

          source: 'subscription_dashboard',

        }, getConfig('desktop.postMessageOrigin'));

      }



      // Method 2: LocalStorage for communication

      localStorage.setItem('subscription_event', JSON.stringify({

        type: eventType,

        data: data,

        timestamp: Date.now(),

      }));



      // Method 3: Custom event

      window.dispatchEvent(new CustomEvent('subscription_event', {

        detail: { type: eventType, data }

      }));



    } catch (error) {

      // Swallow desktop integration errors to avoid leaking data in console

    }

  };



  // Handle desktop app integration

  useEffect(() => {

    if (getConfig('desktop.integrationEnabled')) {

      // Listen for messages from desktop app

      const handleMessage = (event) => {

        if (event.data && event.data.source === 'desktop_app') {

          switch (event.data.type) {

            case 'refresh_subscription':

              refreshData();

              break;

            case 'manual_update':

              manualUpdateSubscription(event.data.data);

              break;

            default:

              break;

          }

        }

      };



      window.addEventListener('message', handleMessage);

      return () => window.removeEventListener('message', handleMessage);

    }

  }, [manualUpdateSubscription, refreshData]);



  // Periodic refresh DISABLED - Only manual refresh is allowed

  // Removed automatic reload to prevent unnecessary API calls

  // Users can manually refresh using the refreshData() function

  // useEffect(() => {

  //   // Automatic periodic refresh removed per user request

  //   // Manual refresh only via refreshData() function

  // }, [loadSubscriptionData]);



  const value = {

    subscription,

    loading,

    error,

    billingHistory,

    usageData,

    updateSubscription,

    cancelSubscription,

    manualUpdateSubscription,

    refreshData,

    forceRefresh,

    getPlanConfig,

    isOnTrial,

    isTrialExpired,

    getTrialDaysRemaining,

    getTrialEndDate,

    hasActiveSubscription,

    getUsagePercentage,

    getRemainingRequests,

    applyCheckoutCredits,

    completeCheckoutReturn,

  };



  return (

    <SubscriptionContext.Provider value={value}>

      {children}

    </SubscriptionContext.Provider>

  );

};

